// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Bar, BarChart, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CHART_COLORS_LIGHT as COLORS } from 'Utils/chartColors';
import { WidgetData } from './dashboardMetrics';

const axisTick = { fontSize: 9, fill: '#9aa7b2' };

// Render a Khora widget's WidgetData — ONLY the kinds add_dashboard_widget produces (kpi / bar / donut /
// table). Non-interactive: used both for the chat's inline preview and the dashboard's pinned widget, so
// the preview looks exactly like what gets pinned. Other WidgetData kinds belong to the legacy auto board.
const WidgetView: React.FC<{ data: WidgetData; height?: number }> = ({ data, height = 150 }) => {
  if (data.kind === 'kpi') {
    return (
      <Box sx={{ textAlign: 'center', py: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.4rem', color: 'common.white', overflowWrap: 'anywhere' }}>{data.value}</Typography>
        {data.help && <Typography sx={{ fontSize: '0.7rem', color: 'grey.400' }}>{data.help}</Typography>}
      </Box>
    );
  }
  if (data.kind === 'bar') {
    const rows = data.x.map((label, index) => ({ label, value: data.y[index] }));
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows}>
          <XAxis dataKey="label" tick={axisTick} />
          <YAxis tick={axisTick} />
          <Tooltip cursor={false} contentStyle={{ fontSize: 11 }} />
          <Bar dataKey="value">
            {rows.map((row, index) => <Cell key={row.label} fill={COLORS[index % COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (data.kind === 'donut') {
    const rows = data.labels.map((label, index) => ({ label, value: data.values[index] }));
    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="label" innerRadius={34} outerRadius={60}>
            {rows.map((row, index) => <Cell key={row.label} fill={COLORS[index % COLORS.length]} />)}
          </Pie>
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <Tooltip contentStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (data.kind === 'table') {
    const columns = data.rows.length ? Object.keys(data.rows[0]) : [];
    return (
      <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 200 }}>
        <Table size="small">
          <TableHead>
            <TableRow>{columns.map((column) => <TableCell key={column} sx={{ color: 'grey.400', fontSize: '0.7rem', borderColor: 'rgba(255,255,255,0.1)', whiteSpace: 'nowrap' }}>{column}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {data.rows.map((row) => (
              <TableRow key={columns.map((column) => String(row[column])).join('|')}>
                {columns.map((column) => <TableCell key={column} sx={{ color: '#eee', fontSize: '0.7rem', borderColor: 'rgba(255,255,255,0.1)', whiteSpace: 'nowrap' }}>{String(row[column] ?? '')}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  }
  return null;
};

export default WidgetView;
